import { signedUrlCacheApi } from "@/app/lib/api/signedUrlCache";
import { storageApi } from "@/app/lib/api/storage";
import { SignedUrlCache, SignedUrlCacheInsert, SignedUrlType } from "@/app/lib/supabase/types";
import { getSignedUrlExpiration } from "@/app/lib/supabase/supabaseStorageConstants";

interface SignedUrlRequest {
  bucket: string;
  filePath: string;
  urlType: SignedUrlType;
}

interface SignedUrlBatchRequest {
  companyId: string;
  userId: string;
  requests: SignedUrlRequest[];
}

interface SignedUrlFolderRequest {
  companyId: string;
  userId: string;
  bucket: string;
  folderPath: string;
  urlType: SignedUrlType;
}

class SignedUrlService {
  private static instance: SignedUrlService;
  private cleanupInterval: NodeJS.Timeout | null = null;

  private constructor() {
    // Start cleanup interval - run every hour
    this.startCleanupInterval();
  }

  public static getInstance(): SignedUrlService {
    if (!SignedUrlService.instance) {
      SignedUrlService.instance = new SignedUrlService();
    }
    return SignedUrlService.instance;
  }

  /**
   * Get a signed URL for a single file
   */
  async getSignedUrl(
    companyId: string,
    userId: string,
    bucket: string,
    filePath: string,
    urlType: SignedUrlType
  ): Promise<string> {
    console.log("Getting signed URL for:", bucket, filePath, urlType);
    try {
      // First, check the database cache
      const cached = await signedUrlCacheApi.getSignedUrl(companyId, bucket, filePath);

      if (cached && new Date(cached.expires_at) > new Date()) {
        return cached.signed_url;
      }

      // If not in cache or expired, generate new signed URL
      const expiresIn = getSignedUrlExpiration(urlType);
      const signedUrl = await storageApi.getSignedUrl(bucket, filePath, expiresIn);
      
      // Calculate folder path from file path
      const folderPath = this.getFolderPath(filePath);

      // Save to cache
      const cacheEntry: SignedUrlCacheInsert = {
        company_id: companyId,
        bucket_name: bucket,
        file_storage_path: filePath,
        folder_storage_path: folderPath,
        signed_url: signedUrl,
        expires_at: new Date(Date.now() + expiresIn * 1000).toISOString(),
        created_by: userId,
        url_type: urlType,
        metadata: null,
      };

      await signedUrlCacheApi.addSignedUrl(cacheEntry);

      return signedUrl;
    } catch (error) {
      console.error("Error getting signed URL:", error);
      throw error;
    }
  }

  /**
   * Get signed URLs for all files in a folder
   */
  async getSignedUrlsForFolder({
    companyId,
    userId,
    bucket,
    folderPath,
    urlType,
  }: SignedUrlFolderRequest): Promise<SignedUrlCache[]> {
    try {
      // First, get all cached URLs for the folder
      const cachedUrls = await signedUrlCacheApi.getSignedUrlsForFolder(
        companyId,
        bucket,
        folderPath
      );

      // Filter out expired URLs
      const validCachedUrls = cachedUrls.filter(
        (url) => new Date(url.expires_at) > new Date()
      );

      // List all files in the folder
      const files = await storageApi.listFiles(bucket, folderPath);

      // Find files that don't have valid cached URLs
      const cachedPaths = new Set(validCachedUrls.map((url) => url.file_storage_path));
      const missingFiles = files.filter(
        (file) => !cachedPaths.has(`${folderPath}/${file.name}`)
      );

      // Generate new signed URLs for missing files
      if (missingFiles.length > 0) {
        const expiresIn = getSignedUrlExpiration(urlType);
        const newCacheEntries: SignedUrlCacheInsert[] = [];

        for (const file of missingFiles) {
          const filePath = `${folderPath}/${file.name}`;
          const signedUrl = await storageApi.getSignedUrl(bucket, filePath, expiresIn);

          newCacheEntries.push({
            company_id: companyId,
            bucket_name: bucket,
            file_storage_path: filePath,
            folder_storage_path: folderPath,
            signed_url: signedUrl,
            expires_at: new Date(Date.now() + expiresIn * 1000).toISOString(),
            created_by: userId,
            url_type: urlType,
            metadata: { size: file.metadata?.size, type: file.metadata?.mimetype },
          });
        }

        const newUrls = await signedUrlCacheApi.addSignedUrlsBatch(newCacheEntries);
        validCachedUrls.push(...newUrls);
      }

      return validCachedUrls;
    } catch (error) {
      console.error("Error getting signed URLs for folder:", error);
      throw error;
    }
  }

  /**
   * Get signed URLs for multiple files (batch operation)
   */
  async getSignedUrlsBatch({
    companyId,
    userId,
    requests,
  }: SignedUrlBatchRequest): Promise<Record<string, string>> {
    try {
      // Get cached URLs for all requests
      const cacheRequests = requests.map((req) => ({
        bucket: req.bucket,
        filePath: req.filePath,
      }));

      const cachedUrls = await signedUrlCacheApi.getSignedUrlsBatch(
        companyId,
        cacheRequests
      );

      // Create a map of cached URLs
      const urlMap: Record<string, string> = {};
      const validCachedUrls = cachedUrls.filter(
        (url) => new Date(url.expires_at) > new Date()
      );

      validCachedUrls.forEach((cached) => {
        const key = `${cached.bucket_name}:${cached.file_storage_path}`;
        urlMap[key] = cached.signed_url;
      });

      // Find requests that need new URLs
      const missingRequests = requests.filter((req) => {
        const key = `${req.bucket}:${req.filePath}`;
        return !urlMap[key];
      });

      // Generate new signed URLs for missing requests
      if (missingRequests.length > 0) {
        const newCacheEntries: SignedUrlCacheInsert[] = [];

        for (const req of missingRequests) {
          const expiresIn = getSignedUrlExpiration(req.urlType);
          const signedUrl = await storageApi.getSignedUrl(
            req.bucket,
            req.filePath,
            expiresIn
          );

          const key = `${req.bucket}:${req.filePath}`;
          urlMap[key] = signedUrl;

          newCacheEntries.push({
            company_id: companyId,
            bucket_name: req.bucket,
            file_storage_path: req.filePath,
            folder_storage_path: this.getFolderPath(req.filePath),
            signed_url: signedUrl,
            expires_at: new Date(Date.now() + expiresIn * 1000).toISOString(),
            created_by: userId,
            url_type: req.urlType,
            metadata: null,
          });
        }

        await signedUrlCacheApi.addSignedUrlsBatch(newCacheEntries);
      }

      // Return URLs in a more convenient format
      const result: Record<string, string> = {};
      requests.forEach((req) => {
        const key = `${req.bucket}:${req.filePath}`;
        result[req.filePath] = urlMap[key];
      });

      return result;
    } catch (error) {
      console.error("Error getting signed URLs batch:", error);
      throw error;
    }
  }

  /**
   * Invalidate cached URLs for specific files
   */
  async invalidateUrls(
    companyId: string,
    bucket: string,
    filePaths: string[]
  ): Promise<void> {
    try {
      await signedUrlCacheApi.deleteSignedUrls(companyId, bucket, filePaths);
    } catch (error) {
      console.error("Error invalidating URLs:", error);
      throw error;
    }
  }

  /**
   * Extract folder path from file path
   */
  private getFolderPath(filePath: string): string {
    const parts = filePath.split("/");
    parts.pop(); // Remove file name
    return parts.join("/");
  }

  /**
   * Start the cleanup interval
   */
  private startCleanupInterval(): void {
    // Run cleanup every hour
    this.cleanupInterval = setInterval(async () => {
      try {
        await signedUrlCacheApi.runCleanupFunction();
      } catch (error) {
        console.error("Error running cleanup:", error);
      }
    }, 60 * 60 * 1000); // 1 hour
  }

  /**
   * Stop the cleanup interval (for cleanup)
   */
  public stopCleanupInterval(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
  }
}

// Export singleton instance
export const signedUrlService = SignedUrlService.getInstance();